import { UserInfoInterface } from '@elearning-platform/api-types/interfaces';
import { Loader, UseState } from '@elearning-platform/ui';
import { QueryClient, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';

import { setAuthTokenTyped, setBaseApiUrlTyped } from '../../api/api';
import { setAuthToken, setBaseApiUrl } from '../../api/default';
import { getUserInfo } from '../../api/requests';
import { AppConfig } from '../../appConfig';
import { AppSettingsActions } from '../../appSettings/redux/appSettings.reducer';
import { selectCurrentBrand } from '../../appSettings/redux/appSettings.selector';
import { PersistGateContext } from '../../contexts';
import { LocalStorageKey, QueryKey } from '../../enums';
import { useLocalStorage } from '../../hooks';
import { OwnDeploymentConfig } from '../../interfaces/internal';
import { UseLocalStorage } from '../../types/internal';
import { UserContextActions } from '../../userContext';

import { Container } from './persistGate.styled';
import { PersistGateProps } from './persistGate.types';

export const PersistGate: React.FC<PersistGateProps> = (props: PersistGateProps): JSX.Element => {
  const { children }: PersistGateProps = props;
  const [userToken]: UseLocalStorage<string | null> = useLocalStorage<string | null>(LocalStorageKey.AuthorizationToken, null);
  const [selectedBrand]: UseLocalStorage<number | null> = useLocalStorage<number | null>(LocalStorageKey.SelectedBrand, null);
  const [isOwnDeploymentApiChanged, setIsOwnDeploymentApiChanged]: UseState<boolean> = useState<boolean>(false);
  const [isApiAuthorized, setIsApiAuthorized]: UseState<boolean> = useState<boolean>(false);
  const [websocketDomain, setWebsocketDomain]: UseState<string | null> = useState<string | null>(null);
  const dispatch: Dispatch = useDispatch();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentBrand: any = useSelector(selectCurrentBrand);
  const queryClient: QueryClient = useQueryClient();

  useEffect((): () => void => {
    const handleContextMenu: (e: MouseEvent) => void = (e: MouseEvent): void => {
      e.preventDefault();
    };
    const handleKeyDown: (e: KeyboardEvent) => void = (e: KeyboardEvent): void => {
      if (
        e.key === 'F12'
        || (e.ctrlKey && e.shiftKey && ['I', 'J', 'C'].includes(e.key.toUpperCase()))
        || (e.ctrlKey && ['S', 'P', 'U'].includes(e.key.toUpperCase()))
      ) {
        e.preventDefault();
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);

    return (): void => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(
    (): void => {
      // cleanup after redux persist
      if (localStorage.getItem('persist:root')) {
        localStorage.removeItem('persist:root');
      }
    },
    []
  );

  const hasCache: boolean = !!queryClient.getQueryData<UserInfoInterface>([QueryKey.UserInfo]);
  const { data: userData }: UseQueryResult<UserInfoInterface> = useQuery({
    queryKey: [QueryKey.UserInfo],
    queryFn: (): Promise<UserInfoInterface> => getUserInfo(),
    enabled: !hasCache && !!userToken.value && isApiAuthorized && (!AppConfig.ownDeployment || isOwnDeploymentApiChanged),
    staleTime: 1_000,
  });

  useEffect(
    (): void => {
      if (userData) {
        dispatch(UserContextActions.fetchUserDataSuccess(userData));
      }
    },
    [userData, dispatch]
  );

  useEffect(
    (): void => {
      if (AppConfig.ownDeployment) {
        void fetch('/api/app-config')
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .then((res: Response): any => res.json())
          .then((res: OwnDeploymentConfig): void => {
            setBaseApiUrlTyped(`https://${res.nodeApiDomain}/api`);
            setBaseApiUrl(`https://${res.phpApiDomain}`, `https://${res.nodeApiDomain}/api`);
            setWebsocketDomain(`wss://${res.nodeApiDomain}`);
            setIsOwnDeploymentApiChanged(true);
          });
      } else {
        setWebsocketDomain(AppConfig.webservice);
      }
    },
    []
  );

  useEffect(
    (): void => {
      if (!userToken.value || isApiAuthorized) {
        return;
      }
      setAuthTokenTyped(userToken.value);
      setAuthToken(userToken.value);
      setIsApiAuthorized(true);
    },
    [userToken.value, isApiAuthorized]
  );

  useEffect(
    (): void => {
      if (selectedBrand.value && !currentBrand) {
        dispatch(AppSettingsActions.selectBrand(selectedBrand.value));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedBrand, dispatch]
  );

  if ((!userToken.isLoading && (!userToken.value || userData)) && (!AppConfig.ownDeployment || isOwnDeploymentApiChanged)) {
    return (
      <PersistGateContext.Provider value={{ websocketDomain }}>
        {children}
      </PersistGateContext.Provider>
    );
  }

  return (
    <Container>
      <Loader />
    </Container>
  );
};
